import { z } from "zod";

/**
 * Money is always an integer count of a currency's minor unit (cents for USD, yen for JPY).
 * One currency per register, chosen by the user (D-011, D-020).
 */
export const MAX_MINOR = 10_000_000;
export const MinorAmount = z.number().int().min(0).max(MAX_MINOR);
export const SignedMinorAmount = z.number().int().min(-MAX_MINOR).max(MAX_MINOR);

export const CURRENCY_CODES = [
  "USD", "CAD", "MXN", "EUR", "GBP", "AUD", "NZD", "JPY", "CHF", "SEK", "NOK", "DKK", "INR", "BRL", "ZAR",
] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export const CurrencyCodeSchema = z.enum(CURRENCY_CODES);

export interface CurrencyInfo {
  code: CurrencyCode;
  /** Digits after the decimal point. 0 for JPY. */
  minorDigits: number;
  /** Common banknotes, in minor units, ascending. Drives quick-tender chips (F-404). */
  notes: readonly number[];
}

export const CURRENCIES: Record<CurrencyCode, CurrencyInfo> = {
  USD: { code: "USD", minorDigits: 2, notes: [100, 500, 1000, 2000, 5000, 10000] },
  CAD: { code: "CAD", minorDigits: 2, notes: [500, 1000, 2000, 5000, 10000] },
  MXN: { code: "MXN", minorDigits: 2, notes: [2000, 5000, 10000, 20000, 50000, 100000] },
  EUR: { code: "EUR", minorDigits: 2, notes: [500, 1000, 2000, 5000, 10000] },
  GBP: { code: "GBP", minorDigits: 2, notes: [500, 1000, 2000, 5000] },
  AUD: { code: "AUD", minorDigits: 2, notes: [500, 1000, 2000, 5000, 10000] },
  NZD: { code: "NZD", minorDigits: 2, notes: [500, 1000, 2000, 5000, 10000] },
  JPY: { code: "JPY", minorDigits: 0, notes: [1000, 2000, 5000, 10000] },
  CHF: { code: "CHF", minorDigits: 2, notes: [1000, 2000, 5000, 10000] },
  SEK: { code: "SEK", minorDigits: 2, notes: [2000, 5000, 10000, 20000, 50000] },
  NOK: { code: "NOK", minorDigits: 2, notes: [5000, 10000, 20000, 50000] },
  DKK: { code: "DKK", minorDigits: 2, notes: [5000, 10000, 20000, 50000] },
  INR: { code: "INR", minorDigits: 2, notes: [1000, 2000, 5000, 10000, 20000, 50000] },
  BRL: { code: "BRL", minorDigits: 2, notes: [200, 500, 1000, 2000, 5000, 10000] },
  ZAR: { code: "ZAR", minorDigits: 2, notes: [1000, 2000, 5000, 10000, 20000] },
};

export const QR_PROVIDERS = ["cashapp", "paypal", "venmo"] as const;
export type QrProvider = (typeof QR_PROVIDERS)[number];
export const QrProviderSchema = z.enum(QR_PROVIDERS);

/** Which currencies each pay-link provider can express in a URL (D-027). */
export const PROVIDER_CURRENCIES: Record<QrProvider, readonly CurrencyCode[]> = {
  venmo: ["USD"],
  cashapp: ["USD", "GBP"],
  paypal: ["USD", "CAD", "MXN", "EUR", "GBP", "AUD", "NZD", "JPY", "CHF", "SEK", "NOK", "DKK", "BRL"],
};

export function providersForCurrency(code: CurrencyCode): QrProvider[] {
  return QR_PROVIDERS.filter((p) => PROVIDER_CURRENCIES[p].includes(code));
}

export function minorToMajor(amountMinor: number, code: CurrencyCode): number {
  return amountMinor / 10 ** CURRENCIES[code].minorDigits;
}

export function majorToMinor(amountMajor: number, code: CurrencyCode): number {
  return Math.round(amountMajor * 10 ** CURRENCIES[code].minorDigits);
}

export function formatMinor(amountMinor: number, code: CurrencyCode, locale?: string): string {
  const digits = CURRENCIES[code].minorDigits;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minorToMajor(amountMinor, code));
}

/** Keypad entry: digits typed right-to-left as minor units ("1250" → 1250 = $12.50; JPY "1250" → ¥1250). */
export function keypadDigitsToMinor(digits: string): number {
  const clean = digits.replace(/\D/g, "").slice(0, 9);
  return clean ? Math.min(parseInt(clean, 10), MAX_MINOR) : 0;
}

/** Quick-tender chips: exact, next whole note, then each common note at or above the total. Ascending, unique. */
export function quickTenderOptions(totalMinor: number, code: CurrencyCode): number[] {
  const { notes, minorDigits } = CURRENCIES[code];
  const unit = 10 ** minorDigits;
  const out = new Set<number>([totalMinor]);
  const nextWhole = Math.ceil(totalMinor / unit) * unit;
  if (nextWhole > totalMinor) out.add(nextWhole);
  for (const n of notes) if (n >= totalMinor) out.add(n);
  const smallest = notes[0] ?? unit;
  const nextNote = Math.ceil(totalMinor / smallest) * smallest;
  if (nextNote > totalMinor) out.add(nextNote);
  return [...out].sort((a, b) => a - b).slice(0, 7);
}
